// Shared option sets and display helpers for the activity tracker.
//
// An activity is tracked by its title; it has no category. Statuses are listed
// here because they are a fixed model `choices` list on the BE and the UI has to
// colour each one.

export const ACTIVITY_STATUSES = ["outstanding", "coming", "done"];

// The status chip's palette. Three states, three clearly distinct readings.
//
// RED IS NOT A STATUS. It belongs to `OVERDUE` below, which is a different
// question — "has this slipped?" rather than "where has it got to?" — and an
// activity can be outstanding and perfectly on time — which is why OVERDUE below
// stays a separate, stronger treatment rather than being folded into this scale.
//
// The three read as a traffic light: done is a mellow green, in-progress a mellow
// yellow, outstanding a mellow red. Outstanding was neutral grey on the argument
// that not-yet-started is the default state of work rather than a problem; the
// call is that a register exists to surface what has not been done, so the eye
// should land on it. Overdue keeps the deeper red plus a border and an icon, so
// "late" still outranks "not started" at a glance.
//
// `coming` keeps its BE value; only its label changed. "Coming" described when
// the work arrives rather than where it is, which is not what the other two say.
// Nothing is migrated — the key on the wire is still `coming`.
export const STATUS_BADGE = {
    outstanding: { label: "Outstanding", color: "#B4453A", bg: "rgba(214,106,94,0.16)" },
    coming: { label: "In progress", color: "#8D6E00", bg: "rgba(255,214,0,0.20)" },
    done: { label: "Done", color: "#2E7D32", bg: "rgba(76,175,80,0.18)" },
};

// The one red on the tracker. An activity whose planned date has passed and is
// not done has slipped, and that is the only thing worth alarming about — which
// is exactly why no status wears this colour.
export const OVERDUE = { color: "#B23A2E", bg: "rgba(255,120,90,0.18)" };

// The BE's default for a new row, and the fallback for a status we don't know.
export const DEFAULT_STATUS = "outstanding";

export const normalizeStatus = (value) => {
    const key = String(value || "").toLowerCase();
    return STATUS_BADGE[key] ? key : DEFAULT_STATUS;
};

// The status chip's words, translated. Keyed by the BE's stable status value.
export const statusLabel = (intl, value) => {
    const key = normalizeStatus(value);
    const id = `activity.status_${key}`;
    return intl.messages?.[id] ? intl.formatMessage({ id }) : STATUS_BADGE[key].label;
};

// The list endpoint returns a bare array, but tolerate a paginated wrapper the
// way every other list on this screen does.
export const asActivities = (data) => (Array.isArray(data) ? data : data?.results || []);

// An AI proposal nobody has accepted yet. `source`/`approved` exist for exactly
// this: a suggested row arrives `ai_suggested` / `approved: false` and waits for
// a human. Once approved it reads as any other activity — the badge marks the
// decision that is still outstanding, not where the row came from.
export const isSuggested = (activity) =>
    activity?.source === "ai_suggested" && !activity?.approved;

// The two answers a suggestion is waiting for, in one palette so the pair reads
// the same on the dashboard and in the impact's own panel. Green accepts, red
// rejects — the only red on a row that is not overdue, and it is earned: reject
// deletes the row.
export const VERDICT = {
    accept: { color: "#2E7D32", bg: "rgba(76,175,80,0.14)" },
    reject: { color: "#B4453A", bg: "rgba(214,106,94,0.14)" },
};

// Evidence is a link or a note. Only http(s) becomes an anchor — a bare "ask
// Dana for the deck" must not render as a broken link, and a `javascript:` or
// `data:` string must never become a clickable one.
export const evidenceHref = (value) => {
    const text = String(value || "").trim();
    if (!/^https?:\/\//i.test(text)) return null;
    try {
        return new URL(text).toString();
    } catch {
        return null;
    }
};

// "2026-10-06" as the reader's own date. The BE sends a plain date with no
// timezone, so it is split by hand: `new Date("2026-10-06")` parses as UTC
// midnight and renders as the 5th for anyone west of Greenwich.
export const formatDate = (intl, value) => {
    if (!value) return "";
    const [year, month, day] = String(value).split("-").map(Number);
    if (!year || !month || !day) return String(value);
    return intl.formatDate(new Date(year, month - 1, day), {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
};

// The dashboard response, defensively: the four totals, the per-impact rollup,
// and the flat activity feed every other view is a fold over.
export const asDashboard = (data) => ({
    totals: {
        outstanding: Number(data?.totals?.outstanding) || 0,
        coming: Number(data?.totals?.coming) || 0,
        done: Number(data?.totals?.done) || 0,
        total: Number(data?.totals?.total) || 0,
    },
    impacts: Array.isArray(data?.impacts) ? data.impacts : [],
    // Denormalised on purpose: each row repeats `impact_name` / `area` /
    // `group_name` so grouping and labelling need no second index.
    activities: Array.isArray(data?.activities) ? data.activities : [],
});


// Has this slipped? Planned in the past and not done. Compared date-only: an
// activity planned for today is not late at 00:01.
export const isOverdue = (activity, today = new Date()) => {
    if (!activity?.planned_date || normalizeStatus(activity.status) === "done") return false;
    const [y, m, d] = String(activity.planned_date).split("-").map(Number);
    if (!y || !m || !d) return false;
    const planned = new Date(y, m - 1, d);
    const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return planned < midnight;
};

// Soonest first. An activity with no planned date sorts LAST rather than first:
// undated is "not scheduled yet", which is behind everything that is, and a null
// sorting to the top would bury the thing actually due tomorrow.
export const byPlannedDate = (a, b) => {
    const left = a?.planned_date || "";
    const right = b?.planned_date || "";
    if (!left && !right) return (a?.id || 0) - (b?.id || 0);
    if (!left) return 1;
    if (!right) return -1;
    return left < right ? -1 : left > right ? 1 : (a?.id || 0) - (b?.id || 0);
};

// done / total for an arbitrary slice of the feed — the per-area bars and the
// per-impact headers are the same sum over different folds.
export const tally = (activities) => {
    const counts = { outstanding: 0, coming: 0, done: 0, total: 0 };
    for (const activity of activities) {
        counts[normalizeStatus(activity.status)] += 1;
        counts.total += 1;
    }
    return counts;
};

// Fold the feed into labelled sections, sorted by planned date within each.
// `keyOf` picks the bucket, `labelOf` names it; the sections themselves come out
// in `order` when one is given, else alphabetically.
export const groupActivities = (activities, keyOf, labelOf, order) => {
    const buckets = new Map();
    for (const activity of activities) {
        const key = keyOf(activity);
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(activity);
    }
    const keys = [...buckets.keys()];
    keys.sort((a, b) => {
        if (order) {
            const ia = order.indexOf(a);
            const ib = order.indexOf(b);
            if (ia !== ib) return (ia < 0 ? order.length : ia) - (ib < 0 ? order.length : ib);
        }
        return String(labelOf(a)).localeCompare(String(labelOf(b)));
    });
    return keys.map((key) => ({
        key,
        label: labelOf(key),
        activities: [...buckets.get(key)].sort(byPlannedDate),
        counts: tally(buckets.get(key)),
    }));
};

// done / total as a whole percentage. Zero activities is 0%, never NaN — and it
// is a real answer, not a missing one: nothing planned is nothing done.
export const donePercent = ({ done = 0, total = 0 } = {}) =>
    total > 0 ? Math.round((Number(done) / Number(total)) * 100) : 0;
